import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentRef, Injectable, InjectionToken, Injector, OnDestroy, inject, signal } from '@angular/core';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { CeSnackbarComponent } from './ce-snackbar.component';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CeOverlayContainerService } from '../ce-overlay/ce-overlay-container.service';
import { Icons } from '../ce-icon/ICONLIB';

// TODO: Use this enum to set the state / style of the snackbar
export enum CeSnackbarState {
    SUCCESS = 'success',
    WARNING = 'warning',
    ERROR = 'error',
    LOADING = 'loading',
    DEFAULT = 'default',
}

export enum CeSnackbarDelays {
    VISIBILITY = 5000, // text-only
    ACTIONS_VISIBILITY = 7000, // with actions or explicit close
    ANIMATION = 300,
}

export interface SnackbarAction {
    label: string;
    onClick: () => void;
}

// TODO: Add more options to the snackbar data (close button, position, etc.)
export interface SnackbarData {
    /**
     * @description The message to be displayed in the snackbar
     * @type {string}
     */
    message: string;

    /**
     * @description The state of the snackbar (success, warning, error, default) to determine the style of the snackbar
     * @todo Currently only the default state is supported and any other state will be ignored
     * @type {CeSnackbarState}
     */
    state?: CeSnackbarState;

    /**
     * @description The duration in milliseconds after which the snackbar will be hidden
     * @type {number | 'persist'}
     */
    duration?: number | 'persist';

    /**
     * @description Whether the snackbar should be closed when the user navigates to a different route
     * @type {boolean}
     * @default true
     */
    closeOnNavigation?: boolean;

    /**
     * @description Icon to display in the snackbar
     * @type {keyof Icons}
     */
    icon?: keyof Icons;

    /**
     * @description Actions to display in the snackbar (e.g., Close button)
     * @type {SnackbarAction[]}
     */
    actions?: SnackbarAction[];

    /**
     * @description Whether to show an explicit close (X) button. Defaults to true for error; otherwise false unless set.
     */
    showCloseButton?: boolean;
}

export const CE_SNACKBAR_DATA = new InjectionToken<SnackbarData>('SNACK_BAR_DATA');

@Injectable({
    providedIn: 'root',
})
export class CeSnackbarService implements OnDestroy {
    #lastOverlayRef: OverlayRef | null = null;
    #timeoutId: ReturnType<typeof setTimeout> | null = null;
    #animationTimeoutId: ReturnType<typeof setTimeout> | null = null;
    #snackbarComponentRef: ComponentRef<CeSnackbarComponent> | null = null;
    #routeChangeSubscription: Subscription;
    #overlayContainerService = inject(CeOverlayContainerService);
    #snackbarOverlayContainer!: HTMLDivElement | null;

    snackbarData = signal<SnackbarData | null>(null);
    // TODO: Add a method for after the snackbar is closed

    constructor(private overlay: Overlay, private injector: Injector, private router: Router) {
        this.#routeChangeSubscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                if (this.snackbarData()?.closeOnNavigation) {
                    this.closeSnackbar();
                }
            }
        });
    }

    showLoadingSnackbar(message: string) {
        this.showSnackbar({
            message,
            state: CeSnackbarState.LOADING,
            duration: 'persist',
            showCloseButton: false,
        });
        return {
            close: () => this.closeSnackbar(),
        };
    }

    ngOnDestroy(): void {
        if (this.snackbarData()?.closeOnNavigation) {
            this.#timeoutId && clearTimeout(this.#timeoutId);
            this.#animationTimeoutId && clearTimeout(this.#animationTimeoutId);
        }
        this.#routeChangeSubscription.unsubscribe();
    }

    showSnackbar(data: SnackbarData) {
        const defaultConfig: SnackbarData = {
            message: data.message,
            closeOnNavigation: true,
        };
        // Keep the default configuration and override only the values that are passed with the data
        const snackbarConfig = { ...defaultConfig, ...data };

        this.snackbarData.set(snackbarConfig);
        // If there is an existing snackbar, close it
        if (this.#lastOverlayRef) {
            this.#lastOverlayRef?.detach();
            this.#lastOverlayRef = null;
            this.#timeoutId && clearTimeout(this.#timeoutId);
        }

        const positionStrategy = this.overlay.position().global().centerHorizontally();

        const overlayConfig = new OverlayConfig({
            hasBackdrop: false,
            positionStrategy,
        });

        this.#lastOverlayRef = this.overlay.create(overlayConfig);

        const injectionTokens = new WeakMap();
        injectionTokens.set(CE_SNACKBAR_DATA, this.snackbarData());
        const injector = new PortalInjector(this.injector, injectionTokens);

        const portal = new ComponentPortal(CeSnackbarComponent, null, injector);
        const snackbarComponentRef = this.#lastOverlayRef.attach(portal);
        this.#snackbarComponentRef = snackbarComponentRef;

        this.#snackbarOverlayContainer = this.#overlayContainerService.createOverlayContainer('snackbar-overlay-container');

        this.#snackbarOverlayContainer.appendChild(this.#lastOverlayRef.hostElement);

        // Auto-dismiss logic
        const current = this.snackbarData();
        if (!current) {
            return;
        }

        // Error snackbars: persist by default (no auto-dismiss) unless explicit numeric duration is set
        if (current.state === CeSnackbarState.ERROR) {
            if (typeof current.duration === 'number') {
                this.#timeoutId = setTimeout(() => {
                    this.closeSnackbar();
                }, current.duration);
            }
            return;
        }

        // Non-error snackbars
        if (current.duration === 'persist') {
            return;
        }

        const hasActions = Boolean(current.actions?.length);
        const hasExplicitClose = current.showCloseButton === true;

        let finalDuration: number;
        if (typeof current.duration === 'number') {
            finalDuration = current.duration;
        } else if (hasActions || hasExplicitClose) {
            finalDuration = CeSnackbarDelays.ACTIONS_VISIBILITY;
        } else {
            finalDuration = CeSnackbarDelays.VISIBILITY;
        }

        this.#timeoutId = setTimeout(() => {
            this.closeSnackbar();
        }, finalDuration);
    }

    closeSnackbar() {
        if (this.#lastOverlayRef) {
            this.#timeoutId && clearTimeout(this.#timeoutId);
            this.#snackbarComponentRef?.instance.close();

            //Wait for the animation to finish before detaching the snackbar
            this.#animationTimeoutId = setTimeout(() => {
                this.#lastOverlayRef?.detach();
                this.#lastOverlayRef = null;
                this.#snackbarComponentRef = null;
                this.#overlayContainerService.removeOverlayContainer(this.#snackbarOverlayContainer);
            }, CeSnackbarDelays.ANIMATION);
        }
    }
}
